import { useState } from "react";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVert from "@mui/icons-material/MoreVert";
import {
  Box,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import type {
  MhaStatus,
  S132Attempt,
  S132Duty,
  S132Period,
  S132Record,
  WithdrawS132AttemptRequest,
} from "@thalamos/aspire-common";
import { latestS132Attempts, S132_DUTY_FOR } from "@thalamos/aspire-common";

import {
  RecordS132AttemptDialog,
  type S132AttemptSubmission,
} from "./RecordS132AttemptDialog.js";
import { periodLabel, s132SectionChoices } from "./s132Sections.js";
import {
  S132CorrectedPill,
  s132DutiesProvision,
  s132DutyProvision,
  S132OutcomePill,
  s132OutcomeText,
  s132PeriodStatusText,
  S132StatusPill,
  s132WithdrawalReasonText,
  S132WithdrawnPill,
} from "./S132StatusPill.js";
import { WithdrawS132AttemptDialog } from "./WithdrawS132AttemptDialog.js";
import {
  Button,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { ukLocalFormatDateTime } from "~/util.js";

export type S132HistoryDialogProps = {
  patientName?: string;
  recorderName?: string;
  record?: S132Record;
  loading: boolean;
  failed: boolean;
  onClose: () => void;
  onSave: (attempt: S132AttemptSubmission) => Promise<S132Record>;
  /** Whether to offer this user the withdraw action on the entry. */
  canWithdraw: (attempt: S132Attempt, record: S132Record) => boolean;
  onWithdraw: (
    attemptId: string,
    request: WithdrawS132AttemptRequest,
  ) => Promise<S132Record>;
  /** Called whenever the record changes, so the opener can refresh its status. */
  onChanged?: (record: S132Record) => void;
};

const muted = { fontSize: "0.75rem", color: "#7B7D89" };

/**
 * An attempt can be recorded before its section is entered, and outlives a
 * section corrected away, so its duty counts as well as the periods'.
 */
const dutiesHeld = (record: S132Record | undefined): S132Duty[] => [
  ...(record?.periods.map((p) => p.duty) ?? []),
  ...(record?.attempts ?? []).flatMap((a) => S132_DUTY_FOR[a.section] ?? []),
];

function periodSummary(period: S132Period): string {
  const count = period.attemptIds.length;
  if (period.status === "notTracked") {
    return `Started before this organisation began tracking ${s132DutyProvision(period.duty)} information.`;
  }
  if (count === 0) {
    return period.current
      ? "No attempts recorded yet. The information is owed until the patient understands it."
      : "No attempts were recorded for this section.";
  }
  const attempts = `${String(count)} attempt${count === 1 ? "" : "s"}`;
  return period.firstUnderstoodAt
    ? `${attempts} · first understood ${ukLocalFormatDateTime(period.firstUnderstoodAt)}`
    : attempts;
}

const givenBy = (attempt: S132Attempt): string =>
  attempt.deliveredByName ?? attempt.recordedByName ?? "unknown user";

function Chevron(props: { open: boolean }) {
  return (
    <ExpandMoreIcon
      fontSize="small"
      sx={{
        color: "#54596B",
        transform: props.open ? "rotate(180deg)" : "none",
        transition: "transform 120ms",
      }}
    />
  );
}

function SupersededEntry(props: {
  attempt: S132Attempt;
  byId: Map<string, S132Attempt>;
}) {
  const { attempt } = props;
  const earlier = attempt.supersedesId
    ? props.byId.get(attempt.supersedesId)
    : undefined;
  return (
    <Box
      data-testid="s132-superseded-attempt"
      sx={{ display: "grid", gap: "4px" }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <S132CorrectedPill />
        <Typography
          sx={{ ...muted, fontSize: "0.8rem", textDecoration: "line-through" }}
        >
          {`${ukLocalFormatDateTime(attempt.occurredAt)} · ${
            attempt.reason ?? s132OutcomeText(attempt.outcome)
          }`}
        </Typography>
      </Box>
      <Typography sx={muted}>
        {`Recorded by ${attempt.recordedByName ?? "unknown user"} · ${ukLocalFormatDateTime(attempt.recordedAt)}`}
      </Typography>
      {earlier && <SupersededEntry attempt={earlier} byId={props.byId} />}
    </Box>
  );
}

/** Edit and Delete sit in a menu so that Record attempt stays the dialog's main action. */
function AttemptActions(props: {
  label: string;
  onCorrect?: () => Promisable<void>;
  onWithdraw?: () => Promisable<void>;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const choose = (action: () => Promisable<void>) => () => {
    setAnchor(null);
    void action();
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={props.label}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={(e) => {
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => {
          setAnchor(null);
        }}
      >
        {props.onCorrect && (
          <MenuItem onClick={choose(props.onCorrect)}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {props.onWithdraw && (
          <MenuItem
            onClick={choose(props.onWithdraw)}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

/** One line per attempt, opening onto the full entry and its corrections. */
function AttemptRow(props: {
  attempt: S132Attempt;
  byId: Map<string, S132Attempt>;
  period?: S132Period;
  initiallyOpen: boolean;
  onCorrect?: () => void;
  onWithdraw?: () => void;
}) {
  const { attempt } = props;
  const [open, setOpen] = useState(props.initiallyOpen);
  const recordedLate =
    !!props.period?.end &&
    dayjs(attempt.recordedAt).isAfter(dayjs(props.period.end));
  const replaced = attempt.supersedesId
    ? props.byId.get(attempt.supersedesId)
    : undefined;

  return (
    <Box data-testid="s132-attempt" sx={{ borderTop: "1px solid #E2E2E6" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <ButtonBase
          aria-expanded={open}
          onClick={() => {
            setOpen(!open);
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "10px 4px",
            textAlign: "left",
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "0.9rem", minWidth: "8.5rem" }}
          >
            {ukLocalFormatDateTime(attempt.occurredAt)}
          </Typography>
          {attempt.withdrawal ? (
            <S132WithdrawnPill />
          ) : (
            <S132OutcomePill outcome={attempt.outcome} />
          )}
          <Typography sx={{ ...muted, flex: 1, minWidth: "8rem" }}>
            {givenBy(attempt)}
          </Typography>
          {(replaced || recordedLate) && (
            <Typography sx={{ ...muted, color: "#6B4E00" }}>
              {[replaced && "Amended", recordedLate && "Recorded late"]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
          <Chevron open={open} />
        </ButtonBase>
        {(props.onCorrect || props.onWithdraw) && (
          <AttemptActions
            label={`Actions for the attempt on ${ukLocalFormatDateTime(attempt.occurredAt)}`}
            onCorrect={props.onCorrect}
            onWithdraw={props.onWithdraw}
          />
        )}
      </Box>

      {open && (
        <Box sx={{ display: "grid", gap: "6px", padding: "0 4px 12px" }}>
          {attempt.withdrawal && (
            <Box
              sx={{
                borderLeft: "2px solid #7B7D89",
                paddingLeft: "8px",
                display: "grid",
                gap: "2px",
              }}
            >
              <Typography sx={{ fontSize: "0.85rem", color: "#0B122C" }}>
                {`Deleted: ${s132WithdrawalReasonText(attempt.withdrawal.reason)}. "${attempt.withdrawal.detail}"`}
              </Typography>
              <Typography sx={muted}>
                {`By ${attempt.withdrawal.withdrawnByName ?? "unknown user"} · ${ukLocalFormatDateTime(attempt.withdrawal.withdrawnAt)} · was ${s132OutcomeText(attempt.outcome).toLowerCase()}`}
              </Typography>
            </Box>
          )}
          {attempt.redacted && (
            <Typography sx={{ ...muted, fontStyle: "italic" }}>
              Details hidden because this entry was recorded against the wrong
              patient.
            </Typography>
          )}
          <Typography sx={{ fontSize: "0.85rem", color: "#424552" }}>
            {[
              attempt.reason && `Reason: ${attempt.reason}`,
              attempt.writtenInformationGiven
                ? "Written information given"
                : "Written information not given",
              attempt.notes,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Typography sx={muted}>
            {`${
              attempt.deliveredByName
                ? `Given by ${attempt.deliveredByName} · recorded by ${attempt.recordedByName ?? "unknown user"}`
                : `Given and recorded by ${attempt.recordedByName ?? "unknown user"}`
            }${attempt.recordedByTeamName ? ` (${attempt.recordedByTeamName})` : ""} · recorded ${ukLocalFormatDateTime(attempt.recordedAt)}`}
          </Typography>
          {recordedLate && (
            <Typography sx={{ ...muted, color: "#6B4E00" }}>
              Recorded after this section ended
            </Typography>
          )}
          {replaced && (
            <Box
              sx={{
                borderLeft: "2px solid #2857A4",
                paddingLeft: "8px",
                display: "grid",
                gap: "4px",
              }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: "#424552" }}>
                {`Reason for amendment: "${attempt.correctionReason ?? ""}"`}
              </Typography>
              <SupersededEntry attempt={replaced} byId={props.byId} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/** Current sections open by default; earlier ones collapse to their summary. */
function PeriodSection(props: {
  period: S132Period;
  attempts: S132Attempt[];
  byId: Map<string, S132Attempt>;
  onCorrect: (attempt: S132Attempt) => void;
  onWithdraw: (attempt: S132Attempt) => (() => void) | undefined;
}) {
  const { period } = props;
  const [open, setOpen] = useState(period.current);

  return (
    <Box
      data-testid="s132-period"
      sx={{ border: "1px solid #E2E2E6", borderRadius: "6px" }}
    >
      <ButtonBase
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          textAlign: "left",
          backgroundColor: "#F8F8F9",
          borderRadius: open ? "6px 6px 0 0" : "6px",
          padding: "10px 12px",
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 600 }}>
            {`${periodLabel(period)}${period.current ? " (current)" : ""}`}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: "#424552" }}>
            {periodSummary(period)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <S132StatusPill
            status={period.status}
            label={s132PeriodStatusText(period)}
          />
          <Chevron open={open} />
        </Box>
      </ButtonBase>
      {open && props.attempts.length > 0 && (
        <Box sx={{ paddingX: "8px" }}>
          {props.attempts.map((attempt, index) => (
            <AttemptRow
              key={attempt.id}
              attempt={attempt}
              byId={props.byId}
              period={period}
              initiallyOpen={index === 0}
              onCorrect={() => {
                props.onCorrect(attempt);
              }}
              onWithdraw={props.onWithdraw(attempt)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * The evidence of every attempt for a patient, grouped by the section each
 * explained. Corrections show the entry they replaced.
 */
export function S132HistoryDialog(props: S132HistoryDialogProps) {
  const [saved, setSaved] = useState<S132Record | undefined>();
  const record = saved ?? props.record;

  const [recording, setRecording] = useState<{
    section?: MhaStatus;
    correcting?: S132Attempt;
  } | null>(null);
  const [withdrawing, setWithdrawing] = useState<S132Attempt | null>(null);
  const [showWithdrawn, setShowWithdrawn] = useState(false);

  const byId = new Map((record?.attempts ?? []).map((a) => [a.id, a]));
  const attemptsFor = (ids: string[]) =>
    ids.map((id) => byId.get(id)).filter((a): a is S132Attempt => !!a);
  const unmatched = attemptsFor(record?.unmatchedAttemptIds ?? []);
  const withdrawn = latestS132Attempts(record?.attempts ?? []).filter(
    (a) => a.withdrawal,
  );
  const withdrawAction = (attempt: S132Attempt) =>
    record && props.canWithdraw(attempt, record)
      ? () => {
          setWithdrawing(attempt);
        }
      : undefined;

  return (
    <>
      <PopupDialog open onClose={props.onClose} maxWidth="md">
        <PopupDialogTitle
          closeDialog={props.onClose}
          titleText={`${s132DutiesProvision(dutiesHeld(record))} information`}
        />
        {props.patientName && (
          <Typography sx={{ fontSize: "0.85rem", color: "#424552", mb: 2 }}>
            {props.patientName}
          </Typography>
        )}

        {props.loading && !record && <LoadingSpinner />}
        {props.failed && !record && (
          <Typography role="alert" sx={{ color: "#C74418" }}>
            The record could not be loaded.
          </Typography>
        )}

        {record && (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {record.periods.length === 0 && unmatched.length === 0 && (
              <Typography sx={{ color: "#424552" }}>
                This patient has no section recorded in eMHA that owes Section
                132 information. You can still record an attempt.
              </Typography>
            )}

            {record.periods.map((period) => (
              <PeriodSection
                key={`${period.section}-${period.start}`}
                period={period}
                attempts={attemptsFor(period.attemptIds)}
                byId={byId}
                onCorrect={(attempt) => {
                  setRecording({ correcting: attempt });
                }}
                onWithdraw={withdrawAction}
              />
            ))}

            {unmatched.length > 0 && (
              <Box sx={{ display: "grid", gap: 0.5, mt: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  Not matched to a recorded section
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#424552" }}>
                  These attempts will count once the section they explained is
                  recorded in eMHA.
                </Typography>
                {unmatched.map((attempt) => (
                  <AttemptRow
                    key={attempt.id}
                    attempt={attempt}
                    byId={byId}
                    initiallyOpen={false}
                    onCorrect={() => {
                      setRecording({ correcting: attempt });
                    }}
                    onWithdraw={withdrawAction(attempt)}
                  />
                ))}
              </Box>
            )}

            {withdrawn.length > 0 && (
              <Box
                data-testid="s132-withdrawn-log"
                sx={{ border: "1px solid #E2E2E6", borderRadius: "6px" }}
              >
                <ButtonBase
                  aria-expanded={showWithdrawn}
                  onClick={() => {
                    setShowWithdrawn(!showWithdrawn);
                  }}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    textAlign: "left",
                    padding: "10px 12px",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {`Deleted entries (${String(withdrawn.length)})`}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#424552" }}>
                      {
                        "Entered in error. Kept for the record; they don't count towards the status."
                      }
                    </Typography>
                  </Box>
                  <Chevron open={showWithdrawn} />
                </ButtonBase>
                {showWithdrawn && (
                  <Box sx={{ paddingX: "8px" }}>
                    {withdrawn.map((attempt) => (
                      <AttemptRow
                        key={attempt.id}
                        attempt={attempt}
                        byId={byId}
                        initiallyOpen={false}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button label="Close" outlined onClick={props.onClose} />
          <Button
            label="Record attempt"
            disabled={!record}
            onClick={() => {
              setRecording({
                section: record?.periods.find((p) => p.current)?.section,
              });
            }}
          />
        </Box>
      </PopupDialog>

      {recording && (
        <RecordS132AttemptDialog
          patientName={props.patientName}
          recorderName={props.recorderName}
          sections={s132SectionChoices(record)}
          defaultSection={recording.section}
          correcting={recording.correcting}
          onClose={() => {
            setRecording(null);
          }}
          onSave={props.onSave}
          onSaved={(updated) => {
            setSaved(updated);
            setRecording(null);
            props.onChanged?.(updated);
          }}
        />
      )}

      {withdrawing && (
        <WithdrawS132AttemptDialog
          attempt={withdrawing}
          onClose={() => {
            setWithdrawing(null);
          }}
          onWithdraw={(request) => props.onWithdraw(withdrawing.id, request)}
          onWithdrawn={(updated) => {
            setSaved(updated);
            setWithdrawing(null);
            props.onChanged?.(updated);
          }}
        />
      )}
    </>
  );
}
